/**
 * This file only exist to cover up for parcel not having the ability to do lazy importing based on
 * a glob. In the future, the big object could essentially be replaced with this:
 *
 * ```js
 * function getLanguage(locale) {
 *   const language = locale.match(/^(.+?)_.+?$/)[1];
 *
 *   return Promise.all([
 *     import(`react-intl/locale-data/${language}`),
 *     import(`./locales/${locale}.json`),
 *   ]);
 * }
 * ```
 *
 * The parcel issue in question can be found here:
 * https://github.com/parcel-bundler/parcel/issues/125
 */

import merge from 'lodash.merge';
import { useEffect, useState } from 'react';
import defaultLocale from './locale/en.json';

/*
1. get language by locale (en-US.json)
2. if fail, extract first part of locale (en.json)
3. get language by language (en.json)
*/
async function getLanguage(locale) {
  return fetch(`/locale/${locale}.json`)
    .then((response) => response.json())
    .catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('🍯 Error', error);
        console.warn('🍯 Using defaultLocale due to error', defaultLocale);
        return defaultLocale;
      }
      throw error;
    });
}

/*
1. load prescreener
2. figure out the system language to use based on locale [94]
3. fetch system language from s3 (en-US.json, not en-US.213xzvb.js) [95]
3b. cache issues??
4. fetch languages from API, overlay the system languages from S3 [96]
*/

/**
 * Returns [loading, messages]
 */
function useMessages(locale, customMessages) {
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (messages !== null) setMessages(null);
    try {
      getLanguage(locale)
        .then((translations) =>
          setMessages(merge({}, translations, customMessages)),
        )
        .catch(() => setMessages(customMessages));
      setError(null);
    } catch (err) {
      setError({
        errorCode: 'locale',
        error: `Locale ${locale} not supported`,
      });
    }
  }, [locale, customMessages]);

  return {
    msg: messages,
    msgLoading: messages === null,
    msgErrorCode: error ? error.errorCode : false,
    msgError: error,
  };
}

export default useMessages;
